/**
 * Strand Mold History Transform
 *
 * Transforms the raw EQPGetPartDetails "location mode" response
 * into strand-specific mold install/uninstall history entries.
 *
 * Key difference from history-transform.ts:
 * - That transform FILTERS OUT mold-level operations (structural)
 * - This transform ONLY KEEPS mold-level operations on a specific strand
 */

import type { PartDetailsHistoryAPI } from "../api/types";

// ============================================
// Types
// ============================================

export interface StrandMoldHistoryEntry {
  readonly TaskId: number;
  readonly Date: Date | null;
  readonly MoldName: string | null;
  readonly MoldPartId: number | null;
  readonly Action: "Installed" | "Uninstalled";
  readonly Heats: number;
  readonly User: string | null;
  readonly Notes: string | null;
}

// ============================================
// Transform
// ============================================

/**
 * Transform raw location-mode history rows into strand mold history entries.
 *
 * Filters to only include:
 * - Install tasks ON this strand (ParentPartId === strandId)
 * - Uninstall tasks FROM this strand (IsDefaultArray && PrevParentPartId === strandId)
 *
 * The SP returns ALL uninstall tasks for molds that have ever been on the strand,
 * including uninstalls from OTHER strands. We filter those out using PrevParentPartId.
 */
export const transformStrandMoldHistory = (
  rows: PartDetailsHistoryAPI[],
  strandId: number,
): StrandMoldHistoryEntry[] => {
  return rows
    .filter((row) => {
      // Install on this strand
      if (!row.IsDefaultArray && row.ParentPartId === strandId) return true;
      // Uninstall FROM this strand (prev location was this strand)
      if (row.IsDefaultArray && row.PrevParentPartId === strandId) return true;
      return false;
    })
    .map((row) => ({
      TaskId: row.TaskId,
      Date: row.Timestamp,
      MoldName: row.PartName,
      MoldPartId: row.PartId ?? null,
      Action: row.IsDefaultArray ? "Uninstalled" : "Installed",
      Heats: row.Heats ?? 0,
      User: row.User,
      Notes: row.Notes,
    }));
};
